import type { Role } from '@bk/contracts';
import { useId, useMemo, useState } from 'react';
import { humanize } from '../../lib/format';
import { Button } from '../../ui';

const normalized = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it')
    .trim()
    .replace(/\s+/g, ' ');

/** Selezione multipla con ricerca: i ruoli assegnati restano visibili anche mentre si filtra l'elenco. */
export function PeopleRolePicker({
  roles,
  value,
  onChange,
}: {
  roles: Role[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const id = useId();
  const [search, setSearch] = useState('');
  const [custom, setCustom] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const labels = useMemo(() => new Map(roles.map((role) => [role.key, role.label])), [roles]);
  const label = (key: string) => labels.get(key) ?? humanize(key);
  const choices = useMemo(
    () =>
      [
        ...new Map([
          ...roles.map((role) => [role.key, role] as const),
          ...value
            .filter((key) => !roles.some((role) => role.key === key))
            .map((key) => [key, { key, label: humanize(key), people: 0 }] as const),
        ]).values(),
      ].sort((a, b) => a.label.localeCompare(b.label, 'it')),
    [roles, value],
  );
  const terms = normalized(search).split(' ').filter(Boolean);
  const visible = choices.filter((role) =>
    terms.every((term) => normalized(`${role.label} ${humanize(role.key)}`).includes(term)),
  );
  const toggle = (key: string) =>
    onChange(value.includes(key) ? value.filter((current) => current !== key) : [...value, key]);
  const addCustom = () => {
    const existing = roles.find((role) => normalized(role.label) === normalized(custom));
    const key =
      existing?.key ??
      normalized(custom)
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 63);
    if (!/^[a-z][a-z0-9_]{0,62}$/.test(key) && !existing) {
      setCustomMessage('Scrivi un nome che inizi con una lettera.');
      return;
    }
    if (value.includes(key)) {
      setCustomMessage('Questo ruolo è già assegnato.');
      return;
    }
    if (value.length >= 100) {
      setCustomMessage('Hai raggiunto il limite di 100 ruoli per persona.');
      return;
    }
    onChange([...value, key]);
    setCustom('');
    setCustomMessage('Ruolo aggiunto alla selezione. Salva la persona per confermarlo.');
  };

  return (
    <fieldset className="min-w-0 space-y-3">
      <legend className="text-sm font-semibold text-ink">Ruoli</legend>
      <p id={`${id}-help`} className="text-xs leading-relaxed text-ink/60">
        Scegli tutti i ruoli che questa persona svolge nell’azienda.
      </p>
      <div className="rounded-xl border border-black/10 bg-white p-3">
        <p className="mb-2 text-xs font-medium text-ink/65">
          {value.length
            ? `${value.length} ${value.length === 1 ? 'ruolo selezionato' : 'ruoli selezionati'}`
            : 'Nessun ruolo assegnato'}
        </p>
        {value.length > 0 && (
          <ul aria-label="Ruoli selezionati" className="flex flex-wrap gap-2">
            {value.map((key) => (
              <li
                key={key}
                className="flex min-w-0 max-w-full items-center gap-1 rounded-lg bg-accent/10 pl-3 text-sm text-accent"
              >
                <span className="min-w-0 break-words py-1.5">{label(key)}</span>
                <button
                  type="button"
                  aria-label={`Rimuovi ruolo ${label(key)}`}
                  className="shrink-0 rounded-lg px-2.5 py-2 font-semibold hover:bg-accent/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                  onClick={() => toggle(key)}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <label className="mt-3 block text-sm" htmlFor={`${id}-search`}>
          <span className="mb-1 block font-medium text-ink/80">Cerca un ruolo</span>
          <input
            id={`${id}-search`}
            type="search"
            autoComplete="off"
            placeholder="Es. amministrazione, vendite, direzione…"
            value={search}
            aria-describedby={`${id}-help ${id}-results`}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.preventDefault();
            }}
            className="w-full rounded-lg border border-black/15 bg-paper px-3 py-2.5 outline-none focus:border-accent/50 focus:ring-2 focus:ring-accent/15"
          />
        </label>
        <p id={`${id}-results`} aria-live="polite" className="my-2 text-xs text-ink/55">
          {search.trim()
            ? `${visible.length} ${visible.length === 1 ? 'ruolo trovato' : 'ruoli trovati'}`
            : `${choices.length} ruoli disponibili`}
        </p>
        <div className="max-h-56 overflow-y-auto overscroll-contain rounded-lg border border-black/[0.06]">
          {visible.length ? (
            visible.map((role) => (
              <label
                key={role.key}
                className={`flex cursor-pointer items-start gap-3 px-3 py-3 text-sm transition hover:bg-accent/5 ${value.includes(role.key) ? 'bg-accent/[0.06]' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={value.includes(role.key)}
                  disabled={!value.includes(role.key) && value.length >= 100}
                  onChange={() => toggle(role.key)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
                />
                <span className="min-w-0 break-words">{role.label}</span>
              </label>
            ))
          ) : (
            <p className="px-3 py-4 text-sm text-ink/60">
              Nessun ruolo corrisponde alla ricerca. Prova un’altra parola oppure aggiungi un ruolo.
            </p>
          )}
        </div>
      </div>
      <details className="rounded-lg border border-dashed border-black/15 bg-white px-3 py-2.5">
        <summary className="cursor-pointer text-sm font-medium text-ink/70">
          Aggiungi un ruolo
        </summary>
        <p className="mb-3 mt-2 text-xs leading-relaxed text-ink/60">
          Se il ruolo non è già nell’elenco, scrivi il suo nome. Verrà assegnato alla persona quando
          salvi.
        </p>
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-end">
          <label className="block w-full min-w-0 flex-1 text-sm" htmlFor={`${id}-custom`}>
            <span className="mb-1 block font-medium text-ink/80">Nome del nuovo ruolo</span>
            <input
              id={`${id}-custom`}
              value={custom}
              maxLength={80}
              onChange={(event) => {
                setCustom(event.target.value);
                setCustomMessage('');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addCustom();
                }
              }}
              className="w-full rounded-lg border border-black/15 bg-paper px-3 py-2 outline-none focus:border-accent/50 focus:ring-2 focus:ring-accent/15"
            />
          </label>
          <Button
            type="button"
            variant="ghost"
            disabled={!custom.trim() || value.length >= 100}
            onClick={addCustom}
          >
            Aggiungi alla selezione
          </Button>
        </div>
        {customMessage && (
          <p role="status" className="mt-2 text-xs text-ink/65">
            {customMessage}
          </p>
        )}
      </details>
    </fieldset>
  );
}
